import type {
  FavoriteProblemsParams,
  ProblemLevel,
  RecommendedProblemsByConceptParams,
  RecommendedProblemsByProblemParams,
} from '~/@entities/Problem'
import type {
  ChapterCustomWorkbookInnerDesignTemplateSearchRequest,
  ConceptChapterCustomWorkbookCreationRequest,
  ConceptChapterCustomWorkbookProblemRecommendationRequest,
  ConceptChapterCustomWorkbookUpdateRequest,
  ConceptStepCustomWorkbookCreationRequest,
  ConceptStepCustomWorkbookProblemRecommendationRequest,
  ConceptStepCustomWorkbookUpdateRequest,
  CoverData as CoverDataWire,
  CustomWorkbookChapterResponseV2,
  CustomWorkbookCopyRequest,
  CustomWorkbookCreationResponse,
  CustomWorkbookInnerDesignTemplateResponseV2,
  CustomWorkbookRecommendedProblemCountResponse,
  CustomWorkbookUpdateResponse,
  CustomWorkbookWithFilterResponse,
  GetFavoriteProblemsData,
  GetRecommendedProblemsByConceptData,
  GetRecommendedProblemsByProblemData,
  SchoolTestProblemStepCustomWorkbookCreationRequest,
  SchoolTestProblemStepCustomWorkbookInnerDesignTemplateSearchRequest,
  SchoolTestProblemStepCustomWorkbookProblemRecommendationRequest,
  SchoolTestProblemStepCustomWorkbookUpdateRequest,
  StepCustomWorkbookInnerDesignTemplateSearchRequest,
  WorkbookProblemChapterCustomWorkbookCreationRequest,
  WorkbookProblemChapterCustomWorkbookProblemRecommendationRequest,
  WorkbookProblemChapterCustomWorkbookUpdateRequest,
  WorkbookProblemStepCustomWorkbookCreationRequest,
  WorkbookProblemStepCustomWorkbookProblemRecommendationRequest,
  WorkbookProblemStepCustomWorkbookUpdateRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestConfig, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'

import {
  adaptCopiedCustomWorkbookId,
  adaptCustomWorkbookChapters,
  adaptCustomWorkbookDetail,
  adaptInnerDesignTemplates,
  adaptPairAndSimilarProblems,
  adaptRecommendedProblemCount,
  adaptSavedCustomWorkbookId,
  adaptTreeProblems,
} from './adapters'
import type {
  CustomWorkbookChapters,
  CustomWorkbookDetail,
  CustomWorkbookFilter,
  CustomWorkbookTreeProblem,
  CustomWorkbookType,
  GroupingChapterType,
  InnerDesignTemplate,
  PairAndSimilarProblems,
  RecommendedProblemCount,
} from './schemas'

// 서버는 customWorkbookType 으로 요청 클래스를 고르는데(@JsonTypeInfo) 스펙은 다섯 요청 타입 모두에 같은 5값
// 유니온을 그려 종류로 좁혀지지 않는다. 종류 → 요청 타입 표로 고르고 자기 종류로 좁힌다.
// 선택 조건 중 앱이 null 로 비워 보내는 것(previousExclusionScope·studentIds·practiceTestRound·
// schoolWorkbookPublishers·limitProblemCountPerConcept·subtitle)은 서버가 nullable 로 받지만 스펙이 optional 로만
// 그려 null 을 되살린다. semester·pairX·similarX 는 서버가 Int 로 받지만 앱이 null 을 보낸다(고등 교재는 학기가
// 없고, 쌍둥이·유사 문제가 없는 원본 교재는 배수가 비어 있다). Jackson 이
// 원시 타입의 null 을 0 으로 받는 기본 동작(FAIL_ON_NULL_FOR_PRIMITIVES=false)에 기대는 것으로 보이며 실행으로는
// 확인하지 않았다.
// 문제 트리(chapters)는 스펙이 id·type 만 그린다. 서버는 추천·조회 응답의 트리를 그대로 다시 받는다.
// 2026-10-08 백엔드 develop 의 workbook/controller/request/CustomWorkbookProblemRecommendationRequest.kt·
// CustomWorkbookCreationRequest.kt·CustomWorkbookUpdateRequestV3.kt·CustomWorkbookInnerDesignTemplateSearchRequest.kt·
// *CustomWorkbookChapterChildRequest.kt 로 확인.
type _Loosen<T, K extends PropertyKey> = Omit<T, K> & Partial<Pick<T, Extract<K, keyof T>>>
type _PdfOptions =
  | 'includeConceptNameInPdf'
  | 'includeAnswerRateInPdf'
  | 'includeLevelInPdf'
  | 'includeTagTopInPdf'
type _SchoolTestAbsent = 'answerTypes' | 'practiceTestOption' | 'level' | 'groupingChapterType'

// 난이도와 실전 모의고사 회차는 서버가 1~5 로 검증하지만(@Min·@Max) 스펙은 정수로만 그린다. 좁혀 둔다.
type _Narrowed = { level: ProblemLevel; practiceTestRound: 1 | 2 | 3 | 4 | 5 }
type _Narrow<T> = {
  [K in keyof T]: K extends keyof _Narrowed
    ? undefined extends T[K]
      ? _Narrowed[K] | undefined
      : _Narrowed[K]
    : T[K]
}
// 서버 요청 클래스가 nullable 로 받는데 스펙이 optional 로만 그린 필드에 null 을 되살린다.
// semester·pairX·similarX 는 예외로 서버가 null 이 될 수 없는 Int 로 받는다. 그래도 앱은 고등(학기 없음)·쌍둥이 문제가
// 없는 교재에서 null 을 보내고, 서버의 Jackson 은 원시 Int 자리의 null 을 0 으로 읽는다(FAIL_ON_NULL_FOR_PRIMITIVES
// 기본값). 0 이 곧 "학기 없음"·"쌍둥이·유사 문제 없음" 이라 그대로 둔다.
// 2026-10-08 백엔드 develop 의 workbook/controller/request/CustomWorkbookCreationRequest.kt·
// CustomWorkbookUpdateRequestV3.kt·CustomWorkbookProblemRecommendationRequest.kt 로 확인.
type _NullableKeys =
  | 'previousExclusionScope'
  | 'studentIds'
  | 'semester'
  | 'pairX'
  | 'similarX'
  | 'practiceTestRound'
  | 'schoolWorkbookPublishers'
  | 'limitProblemCountPerConcept'
  | 'subtitle'
type _WithNull<T> = Omit<T, _NullableKeys> & {
  [K in keyof Pick<T, Extract<keyof T, _NullableKeys>>]:
    | Pick<T, Extract<keyof T, _NullableKeys>>[K]
    | null
}

// 분배 조건부 타입으로 둔다. 제네릭 T 위에서도 다섯 종류의 유니온으로 풀려 customWorkbookType 으로 좁힐 수 있다.
type _ByType<
  T extends CustomWorkbookType,
  Map extends Record<CustomWorkbookType, unknown>,
> = T extends CustomWorkbookType
  ? _WithNull<_Narrow<Omit<Map[T], 'customWorkbookType'>>> & {
      customWorkbookType: T
    }
  : never

type _RecommendationRequests = {
  CONCEPT_CHAPTER: ConceptChapterCustomWorkbookProblemRecommendationRequest
  CONCEPT_STEP: ConceptStepCustomWorkbookProblemRecommendationRequest
  WORKBOOK_PROBLEM_CHAPTER: WorkbookProblemChapterCustomWorkbookProblemRecommendationRequest
  WORKBOOK_PROBLEM_STEP: WorkbookProblemStepCustomWorkbookProblemRecommendationRequest
  SCHOOL_TEST_PROBLEM_STEP: _Loosen<
    SchoolTestProblemStepCustomWorkbookProblemRecommendationRequest,
    _SchoolTestAbsent
  >
}
type _CreationRequests = {
  CONCEPT_CHAPTER: _Loosen<ConceptChapterCustomWorkbookCreationRequest, _PdfOptions>
  CONCEPT_STEP: _Loosen<
    ConceptStepCustomWorkbookCreationRequest,
    _PdfOptions | 'groupingChapterType' | 'showLittleChapterTheory'
  >
  WORKBOOK_PROBLEM_CHAPTER: _Loosen<
    WorkbookProblemChapterCustomWorkbookCreationRequest,
    _PdfOptions | 'level'
  >
  WORKBOOK_PROBLEM_STEP: _Loosen<
    WorkbookProblemStepCustomWorkbookCreationRequest,
    _PdfOptions | 'groupingChapterType' | 'showLittleChapterTheory'
  >
  SCHOOL_TEST_PROBLEM_STEP: _Loosen<
    SchoolTestProblemStepCustomWorkbookCreationRequest,
    _SchoolTestAbsent | 'showLittleChapterTheory'
  >
}
type _UpdateRequests = {
  CONCEPT_CHAPTER: _Loosen<ConceptChapterCustomWorkbookUpdateRequest, _PdfOptions>
  CONCEPT_STEP: _Loosen<
    ConceptStepCustomWorkbookUpdateRequest,
    _PdfOptions | 'groupingChapterType' | 'showLittleChapterTheory'
  >
  WORKBOOK_PROBLEM_CHAPTER: _Loosen<
    WorkbookProblemChapterCustomWorkbookUpdateRequest,
    _PdfOptions | 'level'
  >
  WORKBOOK_PROBLEM_STEP: _Loosen<
    WorkbookProblemStepCustomWorkbookUpdateRequest,
    _PdfOptions | 'groupingChapterType' | 'showLittleChapterTheory'
  >
  SCHOOL_TEST_PROBLEM_STEP: _Loosen<
    SchoolTestProblemStepCustomWorkbookUpdateRequest,
    _SchoolTestAbsent | 'showLittleChapterTheory'
  >
}
type _InnerDesignTemplateRequests = {
  CONCEPT_CHAPTER: ChapterCustomWorkbookInnerDesignTemplateSearchRequest
  CONCEPT_STEP: StepCustomWorkbookInnerDesignTemplateSearchRequest
  WORKBOOK_PROBLEM_CHAPTER: ChapterCustomWorkbookInnerDesignTemplateSearchRequest
  WORKBOOK_PROBLEM_STEP: StepCustomWorkbookInnerDesignTemplateSearchRequest
  SCHOOL_TEST_PROBLEM_STEP: SchoolTestProblemStepCustomWorkbookInnerDesignTemplateSearchRequest
}

// 스펙은 단계(steps)의 원소를 이름 없는 object 로 그린다(CustomWorkbookStep 이 인터페이스라서). 문제 조건의 단계로 덮는다.
type _WithTree<Body, T extends CustomWorkbookType, G extends GroupingChapterType> = Omit<
  Body,
  'chapters' | 'coverData' | 'steps'
> & {
  chapters: CustomWorkbookChapters<T, G>
  coverData: WithNullableOptionals<CoverDataWire>
} & Partial<Pick<CustomWorkbookFilter<T>, Extract<keyof CustomWorkbookFilter<T>, 'steps'>>>

/** 추천 문제 수·추천 문제 조회의 조건 */
export type RecommendationBody<T extends CustomWorkbookType = CustomWorkbookType> = _ByType<
  T,
  _RecommendationRequests
>
/** 내 교재 저장. 공유 범위(shareScope)는 만들 때만 받는다 */
export type CustomWorkbookCreateBody<
  T extends CustomWorkbookType = CustomWorkbookType,
  G extends GroupingChapterType = GroupingChapterType,
> = _WithTree<_ByType<T, _CreationRequests>, T, G>
export type CustomWorkbookUpdateBody<
  T extends CustomWorkbookType = CustomWorkbookType,
  G extends GroupingChapterType = GroupingChapterType,
> = _WithTree<_ByType<T, _UpdateRequests>, T, G>
export type InnerDesignTemplateBody<T extends CustomWorkbookType = CustomWorkbookType> = _ByType<
  T,
  _InnerDesignTemplateRequests
>
export type CustomWorkbookCopyBody = CustomWorkbookCopyRequest

// 이 컨트롤러(/workbook/custom)는 CommonResponse 봉투 없이 본문을 바로 낸다.
const PATH = '/workbook/custom'

// 조건을 바꿀 때마다 다시 세고 다시 추천하므로 앞선 요청을 버릴 수 있게 signal 을 받는다
type _Config = Pick<RequestConfig, 'meta' | 'signal'>

export const getRecommendedProblemCount = async <T extends CustomWorkbookType>(
  body: RecommendationBody<T>,
  config?: _Config,
): Promise<RecommendedProblemCount<T>> =>
  adaptRecommendedProblemCount(
    await httpClient().post<CustomWorkbookRecommendedProblemCountResponse>(
      `${PATH}/problem-count`,
      body,
      config,
    ),
    body.customWorkbookType as T,
  )

export const getRecommendedProblems = async <
  T extends CustomWorkbookType,
  G extends GroupingChapterType = GroupingChapterType,
>(
  body: RecommendationBody<T>,
  config?: _Config,
): Promise<CustomWorkbookChapters<T, G>> =>
  adaptCustomWorkbookChapters<T, G>(
    await httpClient().post<CustomWorkbookChapterResponseV2>(`${PATH}/problem`, body, config),
    body.customWorkbookType as T,
    ('groupingChapterType' in body ? body.groupingChapterType : undefined) as G | undefined,
  )

/** 저장된 내 교재의 문제 트리. 트리 모양을 정하는 만드는 방식·묶는 단위는 내 교재 상세의 filter 가 갖는다 */
export const getCustomWorkbookChapters = async <
  T extends CustomWorkbookType,
  G extends GroupingChapterType = GroupingChapterType,
>(
  workbookId: CustomWorkbookDetail['id'],
  { customWorkbookType, groupingChapterType }: { customWorkbookType: T; groupingChapterType?: G },
  meta?: RequestMeta,
): Promise<CustomWorkbookChapters<T, G>> =>
  adaptCustomWorkbookChapters<T, G>(
    await httpClient().get<CustomWorkbookChapterResponseV2>(`${PATH}/${workbookId}/problem`, {
      meta,
    }),
    customWorkbookType,
    groupingChapterType,
  )

export const getCustomWorkbookDetail = async (
  workbookId: CustomWorkbookDetail['id'],
  config?: _Config,
): Promise<CustomWorkbookDetail> =>
  adaptCustomWorkbookDetail(
    await httpClient().get<CustomWorkbookWithFilterResponse>(`${PATH}/${workbookId}`, config),
  )

export const createCustomWorkbook = async <
  T extends CustomWorkbookType,
  G extends GroupingChapterType = GroupingChapterType,
>(
  body: CustomWorkbookCreateBody<T, G>,
  meta?: RequestMeta,
): Promise<CustomWorkbookDetail['id']> =>
  adaptSavedCustomWorkbookId(
    await httpClient().post<CustomWorkbookCreationResponse>(PATH, body, { meta }),
  )

export const updateCustomWorkbook = async <
  T extends CustomWorkbookType,
  G extends GroupingChapterType = GroupingChapterType,
>(
  workbookId: CustomWorkbookDetail['id'],
  body: CustomWorkbookUpdateBody<T, G>,
  meta?: RequestMeta,
): Promise<CustomWorkbookDetail['id']> =>
  adaptSavedCustomWorkbookId(
    await httpClient().put<CustomWorkbookUpdateResponse>(`${PATH}/${workbookId}`, body, { meta }),
  )

export const getInnerDesignTemplates = async <T extends CustomWorkbookType>(
  body: InnerDesignTemplateBody<T>,
  meta?: RequestMeta,
): Promise<InnerDesignTemplate[]> =>
  adaptInnerDesignTemplates(
    await httpClient().post<CustomWorkbookInnerDesignTemplateResponseV2[]>(
      `${PATH}/inner-design-template`,
      body,
      { meta },
    ),
  )

/** 내 교재를 다른 공유 범위(학원·학원 그룹)로 복제한다 */
export const copyCustomWorkbook = async (
  workbookId: CustomWorkbookDetail['id'],
  body: CustomWorkbookCopyBody,
  meta?: RequestMeta,
): Promise<CustomWorkbookDetail['id']> =>
  adaptCopiedCustomWorkbookId(
    await httpClient().post<number>(`${PATH}/${workbookId}/copy`, body, { meta }),
  )

/***************** 교재에 더할 문제 *****************/
// 교재 만들기 트리에 바로 넣을 문제 칸 모양으로 온다. 요청 조건 타입은 Problem 이 발행한다.

/** 고른 유형들에서 추천 문제를 받는다 */
export const getRecommendedProblemsByConcept = async (
  body: RecommendedProblemsByConceptParams,
  meta?: RequestMeta,
): Promise<CustomWorkbookTreeProblem[]> =>
  adaptTreeProblems(
    await httpClient().post<GetRecommendedProblemsByConceptData>(
      '/problems/recommend/by-concept',
      body,
      { meta },
    ),
  )

/** 기준 문제의 쌍둥이·유사 문제를 받는다 */
export const getRecommendedProblemsByProblem = async (
  body: RecommendedProblemsByProblemParams,
  meta?: RequestMeta,
): Promise<PairAndSimilarProblems> =>
  adaptPairAndSimilarProblems(
    await httpClient().post<GetRecommendedProblemsByProblemData>(
      '/problems/recommend/by-problem',
      body,
      { meta },
    ),
  )

/** 즐겨찾기한 문제 중 조건에 맞는 것을 받는다 */
export const getFavoriteProblems = async (
  body: FavoriteProblemsParams,
  meta?: RequestMeta,
): Promise<CustomWorkbookTreeProblem[]> =>
  adaptTreeProblems(
    await httpClient().post<GetFavoriteProblemsData>('/problems/favorites', body, { meta }),
  )
